import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HiOutlineCheckCircle,
  HiOutlineShoppingBag,
  HiOutlineArrowRight,
  HiSparkles,
  HiOutlineTicket, // Icon for subscriptions
} from "react-icons/hi2";
import SummaryApi from "../common";

const OrderSuccess = () => {
  const [isSaving, setIsSaving] = useState(true);
  const [type, setType] = useState("order"); // 'order' or 'subscription'
  const hasSaved = useRef(false);
  const getNimbusToken = async () => {
    try {
      const res = await fetch(SummaryApi.nimbusLogin.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: "sureshit@oakyaitservices.com", // Ensure this is your Nimbus email
          password: "Eveya@123", // Ensure this is your Nimbus password
        }),
      });
      const result = await res.json();

      // NimbusPost returns { status: true, data: "your_token_string" }
      if (result.status) {
        return result.data;
      }
    } catch (err) {
      console.error("Nimbus Post Login Failed", err);
    }
    return null;
  };

  const createNimbusShipment = async (orderId, orderData) => {
    // 1. Get the token dynamically
    const token = await getNimbusToken();

    if (!token) {
      console.error("Shipment aborted: No Nimbus Token");
      return { status: false };
    }

    // 2. Prepare Payload
    const payload = {
      order_number: String(orderId),
      order_date: new Date().toISOString().split("T")[0],
      order_type: "Prepaid",
      payment_type: "prepaid",
      consignee: {
        name: orderData.user_name,
        address: orderData.order_address,
        city: orderData.city || "Mumbai",
        state: orderData.state || "Maharashtra",
        pincode: orderData.pincode || "400001",
        phone: orderData.mobile_number,
      },
      pickup_location: "PRIMARY HYD WAREHOUSE",
      order_amount: orderData.total_amount,
      package_weight: 500,
      package_length: 10,
      package_width: 10,
      package_height: 5,
      order_items: (orderData.cartItems || []).map((item) => ({
        name: item.product_name || "Product",
        qty: item.quantity,
        price: item.price,
        sku: String(item.product_id),
      })),
    };

    try {
      const response = await fetch(SummaryApi.createShipment.url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // Use the token here
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      return await response.json();
    } catch (error) {
      console.error("Nimbus Shipment Error:", error);
      return { status: false };
    }
  };
  // Helper to update local session so the UI reflects subscriber status immediately
  const updateLocalUserSession = (planId, packageId) => {
    try {
      const sessionData = localStorage.getItem("user");
      if (!sessionData) return;
      const parsedUser = JSON.parse(sessionData);
      const updatedUser = {
        ...parsedUser,
        is_subscriber: 1,
        active_plan_id: planId,
        active_package_id: packageId,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new Event("userUpdate"));
      console.log("Local Storage Updated: User is now a subscriber");
    } catch (e) {
      console.error("Session update error", e);
    }
  };

  useEffect(() => {
    const finalizePayment = async () => {
      if (hasSaved.current) return;

      const pendingOrderData = localStorage.getItem("pendingOrder");
      const pendingSubData = localStorage.getItem("pendingSubscription");

      try {
        // --- CASE 1: SUBSCRIPTION SUCCESS ---
        if (pendingSubData) {
          setType("subscription");
          const subData = JSON.parse(pendingSubData);

          const response = await fetch(SummaryApi.subscriptionCreate.url, {
            method: SummaryApi.subscriptionCreate.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...subData,
              is_payment_success: 1, // Mark as paid
            }),
          });

          const result = await response.json();
          if (result.status) {
            updateLocalUserSession(subData.plan_id, subData.package_id);
            localStorage.removeItem("pendingSubscription");
            hasSaved.current = true;
          }
        }

        // --- CASE 2: REGULAR ORDER SUCCESS ---
        else if (pendingOrderData) {
          setType("order");
          const orderData = JSON.parse(pendingOrderData);

          const response = await fetch(SummaryApi.placeOrder.url, {
            method: SummaryApi.placeOrder.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...orderData,
              order_status: "placed",
              payment_status: "paid",
            }),
          });

          const result = await response.json();
          if (result.status) {
            const orderId = result.order_id || orderData.order_id;
            // await createNimbusShipment(orderId, orderData);
            localStorage.removeItem("pendingOrder");
            sessionStorage.removeItem("guest_cart");
            hasSaved.current = true;
          }
        }
      } catch (error) {
        console.error("Error finalizing payment:", error);
      }

      setIsSaving(false);
      window.dispatchEvent(new Event("cartUpdate"));
      window.scrollTo(0, 0);
    };

    finalizePayment();
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF0F3] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
          className="bg-white rounded-[3rem] p-10 text-center shadow-2xl shadow-rose-200 border border-white"
        >
          {isSaving ? (
            <div className="py-10 flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-rose-500 mb-4"></div>
              <p className="text-rose-950 font-black uppercase tracking-widest text-sm">
                Confirming Payment...
              </p>
            </div>
          ) : (
            <>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-24 h-24 bg-rose-500 rounded-[2rem] flex items-center justify-center mx-auto mb-8 shadow-xl shadow-rose-200"
              >
                <HiOutlineCheckCircle size={48} className="text-white" />
              </motion.div>

              <h1 className="text-4xl font-black text-rose-950 uppercase tracking-tighter mb-4">
                {type === "subscription" ? "Subscription" : "Payment"} <br />
                Successful!
              </h1>

              <div className="flex items-center justify-center gap-2 text-rose-500 mb-8 font-bold uppercase text-[10px] tracking-widest">
                <HiSparkles />
                <span>
                  {type === "subscription"
                    ? "Welcome to the Eveya Family"
                    : "Your order has been placed"}
                </span>
                <HiSparkles />
              </div>

              <p className="text-rose-900/50 text-sm leading-relaxed mb-10">
                {type === "subscription"
                  ? "Your subscription is now active. You can now enjoy exclusive benefits and track your pad balance in your profile."
                  : "We've received your payment. Your box is being prepared for delivery and will reach you soon!"}
              </p>

              <div className="space-y-4">
                {type === "subscription" ? (
                  <Link
                    to="/my-subscriptions"
                    className="flex items-center justify-center gap-3 w-full bg-rose-950 text-white py-5 rounded-[2rem] font-black uppercase tracking-widest text-sm hover:bg-rose-900 transition-all shadow-lg"
                  >
                    <HiOutlineTicket size={20} />
                    My Subscriptions
                  </Link>
                ) : (
                  <Link
                    to="/my-orders"
                    className="flex items-center justify-center gap-3 w-full bg-rose-950 text-white py-5 rounded-[2rem] font-black uppercase tracking-widest text-sm hover:bg-rose-900 transition-all shadow-lg"
                  >
                    <HiOutlineShoppingBag size={20} />
                    View My Orders
                  </Link>
                )}

                <Link
                  to="/products"
                  className="flex items-center justify-center gap-2 w-full text-rose-500 font-black uppercase tracking-widest text-xs hover:text-rose-600 transition-all py-2"
                >
                  Continue Shopping
                  <HiOutlineArrowRight />
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default OrderSuccess;
